import { useEffect, useRef, useState } from 'react';
import { Calendar, Clock, ArrowRight, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getBlogPosts } from '@/lib/contentful';
import type { Entry } from 'contentful';
import type { BlogPostSkeleton } from '@/lib/contentful';
import { Footer } from '@/sections/Footer';
import { SEO } from '@/components/SEO';

gsap.registerPlugin(ScrollTrigger);

// 'Todas' is the "show all" filter; the rest are the real blog categories.
// Kept in sync with the Contentful `category` field values (compared trimmed,
// so trailing whitespace in Contentful data doesn't break filtering).
const categories = ['Todas', 'Noticias', 'Regulaciones', 'Guías', 'Consejos', 'Agencias'];

export function Blog() {
  const headerRef = useRef<HTMLDivElement>(null);
  const postsRef = useRef<HTMLDivElement>(null);
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const [posts, setPosts] = useState<Entry<BlogPostSkeleton>[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBlogPosts()
      .then(setPosts)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out',
          scrollTrigger: { trigger: headerRef.current, start: 'top 80%' } }
      );
      const postCards = postsRef.current?.querySelectorAll('.blog-card');
      if (postCards) {
        gsap.fromTo(
          postCards,
          { y: 40, opacity: 0 },
          { y: 0, opacity: 1, stagger: 0.08, duration: 0.5, ease: 'power2.out',
            scrollTrigger: { trigger: postsRef.current, start: 'top 75%' } }
        );
      }
    });
    return () => ctx.revert();
  }, [loading]);

  const filteredPosts = selectedCategory === 'Todas'
    ? posts
    : posts.filter((p) => String(p.fields.category ?? '').trim() === selectedCategory);

  return (
    <main className="bg-bg-light">
      <SEO
        title="Blog | Guías y Consejos para Enviar a Cuba"
        description="Guías prácticas de envío, consejos anti-fraude, noticias y recursos para clientes que envían paquetes y productos a Cuba."
        path="/blog"
        keywords="blog envíos Cuba, guías envío Cuba, consejos enviar a Cuba, noticias Mi Envío Cuba"
      />
      {/* Header */}
      <div ref={headerRef} className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 rounded-2xl bg-brand/10 flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-8 h-8 text-brand" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-text-primary mb-6">
            Blog
          </h1>
          <p className="text-lg text-text-secondary">
            Guías, consejos y noticias sobre envíos a Cuba.
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="pb-8 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-brand text-white'
                  : 'bg-white text-text-secondary hover:text-text-primary'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Blog Posts */}
      <div ref={postsRef} className="pb-24 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-24">
              <div className="w-8 h-8 rounded-full border-2 border-brand border-t-transparent animate-spin" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <p className="text-center text-text-muted py-24">No hay artículos en esta categoría.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post) => {
                const { title, excerpt, category, publishDate, readTime, coverImage, slug } = post.fields;
                const imageUrl = coverImage && 'fields' in coverImage
                  ? `https:${(coverImage as { fields: { file: { url: string } } }).fields.file.url}`
                  : '/hero_delivery.webp';
                const formattedDate = publishDate
                  ? new Date(publishDate as string).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
                  : '';

                return (
                  <article
                    key={post.sys.id}
                    className="blog-card bg-white rounded-[24px] shadow-card overflow-hidden hover:shadow-lg transition-shadow group"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={imageUrl}
                        alt={title as string}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="px-3 py-1 rounded-full bg-brand/10 text-brand text-xs font-medium">
                          {String(category ?? '').trim()}
                        </span>
                        {formattedDate && (
                          <span className="flex items-center gap-1 text-xs text-text-muted">
                            <Calendar className="w-3 h-3" />
                            {formattedDate}
                          </span>
                        )}
                      </div>
                      <h2 className="font-heading text-lg font-semibold text-text-primary mb-2 line-clamp-2">
                        {title as string}
                      </h2>
                      <p className="text-text-secondary text-sm mb-4 line-clamp-2">
                        {excerpt as string}
                      </p>
                      <div className="flex items-center justify-between">
                        {readTime && (
                          <span className="flex items-center gap-1 text-xs text-text-muted">
                            <Clock className="w-3 h-3" />
                            {readTime as string}
                          </span>
                        )}
                        <Link
                          to={`/blog/${slug as string}`}
                          className="flex items-center gap-1 text-sm text-brand font-medium group-hover:underline ml-auto"
                        >
                          Leer más
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </main>
  );
}
